import { Controller } from "@hotwired/stimulus";

// Turbo's built-in progress bar only activates for real Drive visits. Frame
// navigations — including ones promoted with data-turbo-action, which this app
// uses for nearly all internal links — fire a history-only visit with
// willRender: false that never issues a request, so Turbo's bar never shows.
// turbo:before-fetch-request/-response fire for every Turbo fetch (Drive or
// Frame), so we drive our own bar off those instead and switch Turbo's native
// bar off so the two don't double up. Reuses Turbo's own .turbo-progress-bar
// element/class, whose base positioning Turbo injects on load regardless.
const SHOW_DELAY = 500;
const FADE_DURATION = 300;
// A superseded request (e.g. a frame navigation promoted to a fresh Drive visit after a
// redirect) never gets a matching turbo:before-fetch-response, so it can't be counted down —
// without a hard ceiling the bar would stay on screen forever.
const MAX_VISIBLE_DURATION = 15000;

export default class extends Controller {
  pendingRequests = 0;
  barElement = null;
  showTimeout = null;
  trickleInterval = null;
  watchdogTimeout = null;
  value = 0;

  connect() {
    Turbo.config.drive.progressBarDelay = Number.MAX_SAFE_INTEGER;
    this.onRequest = this.onRequest.bind(this);
    this.onResponse = this.onResponse.bind(this);
    this.onVisit = this.onVisit.bind(this);
    document.addEventListener("turbo:before-fetch-request", this.onRequest);
    document.addEventListener("turbo:before-fetch-response", this.onResponse);
    document.addEventListener("turbo:visit", this.onVisit);
  }

  disconnect() {
    document.removeEventListener("turbo:before-fetch-request", this.onRequest);
    document.removeEventListener("turbo:before-fetch-response", this.onResponse);
    document.removeEventListener("turbo:visit", this.onVisit);
    this.hide();
  }

  // A new top-level visit means whatever request(s) were pending before it got superseded and
  // will never resolve, so their counts would otherwise never come back down.
  onVisit() {
    this.pendingRequests = 0;
  }

  onRequest() {
    this.pendingRequests += 1;
    if (this.showTimeout || this.barElement) return;

    this.showTimeout = window.setTimeout(() => this.show(), SHOW_DELAY);
  }

  onResponse() {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    if (this.pendingRequests === 0) this.hide();
  }

  show() {
    this.showTimeout = null;
    this.barElement = document.createElement("div");
    this.barElement.className = "turbo-progress-bar";
    this.barElement.style.opacity = "1";
    document.documentElement.insertBefore(this.barElement, document.body);
    this.setValue(0);
    this.trickleInterval = window.setInterval(() => this.setValue(this.value + Math.random() / 100), FADE_DURATION);
    this.watchdogTimeout = window.setTimeout(() => {
      this.pendingRequests = 0;
      this.hide();
    }, MAX_VISIBLE_DURATION);
  }

  setValue(value) {
    this.value = value;
    const element = this.barElement;
    requestAnimationFrame(() => {
      if (element) element.style.width = `${10 + this.value * 90}%`;
    });
  }

  hide() {
    if (this.showTimeout) window.clearTimeout(this.showTimeout);
    this.showTimeout = null;
    if (this.watchdogTimeout) window.clearTimeout(this.watchdogTimeout);
    this.watchdogTimeout = null;
    if (!this.barElement) return;

    window.clearInterval(this.trickleInterval);
    const element = this.barElement;
    this.barElement = null;
    element.style.opacity = "0";
    window.setTimeout(() => element.remove(), FADE_DURATION * 1.5);
  }
}
